import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { SOULMARK } from "@/constants/testIds";
import ShareSoulMark from "@/components/ShareSoulMark";

/**
 * SoulMarkCard — displays (or triggers generation of) the seeker's unique
 * AI-generated card back. This is the ONLY generated artefact in the new
 * deck architecture — all 78 faces are proprietary CardFace SVGs.
 */
export default function SoulMarkCard() {
  const [mark, setMark] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [shareCtx, setShareCtx] = useState({ title: "", soulName: "", sentence: "" });

  useEffect(() => {
    api
      .get("/deck/soul-mark")
      .then((r) => setMark(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
    api
      .get("/profile/me")
      .then((r) => {
        const soul = r.data?.soul_record || {};
        const reveal = soul.character_reveal || {};
        setShareCtx({
          title: reveal.archetype_title || "",
          soulName: soul.profile?.soul_name || r.data?.user?.name || "",
          sentence: reveal.body || reveal.blessing || "",
        });
      })
      .catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/deck/soul-mark/generate");
      setMark(data);
      toast.success("Your Soul Mark has been drawn from the black.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Soul Mark could not surface.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      data-testid={SOULMARK.card}
      className="rounded-2xl overflow-hidden flex flex-col md:flex-row items-stretch"
      style={{
        background:
          "linear-gradient(135deg, rgba(20,17,10,0.6) 0%, rgba(8,8,8,0.9) 100%)",
        border: "1px solid rgba(212,175,55,0.28)",
        boxShadow: "0 22px 60px rgba(0,0,0,0.55)",
      }}
    >
      <div
        className="flex items-center justify-center p-6 md:p-8 relative"
        style={{
          background:
            "radial-gradient(circle at center, rgba(212,175,55,0.08), transparent 65%)",
          minWidth: "clamp(200px, 40vw, 300px)",
        }}
      >
        {mark ? (
          <motion.img
            data-testid={SOULMARK.image}
            src={mediaUrl(mark.image_url)}
            alt="Your Soul Mark"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="rounded-2xl w-full"
            style={{
              maxWidth: 240,
              aspectRatio: "1 / 1",
              objectFit: "cover",
              border: "2px solid rgba(212,175,55,0.5)",
              boxShadow: "0 0 40px rgba(212,175,55,0.25), 0 22px 40px rgba(0,0,0,0.55)",
            }}
          />
        ) : (
          <div
            className="rounded-2xl flex items-center justify-center text-center px-6"
            style={{
              width: 240,
              aspectRatio: "1 / 1",
              background: "#080808",
              border: "1px dashed rgba(212,175,55,0.35)",
              color: "rgba(244,239,230,0.4)",
            }}
          >
            <div>
              <Sparkles size={22} strokeWidth={1.5} className="mx-auto mb-2" style={{ color: "#D4AF37" }} />
              <div className="font-overline text-[10px] tracking-[0.24em]">
                {loaded ? "Awaiting your Soul Mark" : "…"}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
        <div className="font-overline text-[#C8A96E] mb-2">The Generated Back</div>
        <h2 className="font-heading italic text-3xl text-[#F4EFE6] font-light leading-tight">
          Your Soul Mark
        </h2>
        <p className="text-sm md:text-base text-[#F4EFE6]/65 leading-relaxed mt-4 max-w-lg">
          A single, one-of-one back design drawn from your natal sky, your
          numerology, your elemental current and the name your soul answers to.
          Intricate botanical filigree and sacred geometry in Golden Light on
          matte black — the same craft as the House Decks, wholly yours. Every
          card in every physical AuraVeil deck you own carries this mark.
        </p>
        {!mark && (
          <button
            data-testid={SOULMARK.generateBtn}
            onClick={generate}
            disabled={generating}
            className="btn-sacred btn-sacred-solid mt-6 self-start disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {generating ? "Drawing from the black…" : "Draw my Soul Mark"}
          </button>
        )}
        {mark && (
          <>
            <div className="mt-6 font-overline text-[10px] text-[#F4EFE6]/40">
              Element · {mark.element} · Sealed{" "}
              {new Date(mark.created_at).toLocaleDateString()}
            </div>
            <div className="mt-6">
              <ShareSoulMark
                imageUrl={mediaUrl(mark.image_url)}
                title={shareCtx.title}
                soulName={shareCtx.soulName}
                sentence={shareCtx.sentence}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
